// components/templates/sections/BlogSection.tsx
'use client';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import Image from 'next/image';
import Link from 'next/link';
import { useDesign } from '@/context/DesignContext';

interface BlogPost {
  image: string;
  day: string;
  month: string;
  title: string;
  author: string;
  category: string;
  excerpt: string;
  link: string;
}

interface BlogSectionProps {
  subtitle: string;
  title: string;
  description: string;
  posts: BlogPost[];
  design?: any;
}

export default function BlogSection({
  subtitle,
  title,
  description,
  posts,
  design: propDesign,
}: BlogSectionProps) {
  const contextDesign = useDesign();
  const design = propDesign || contextDesign;

  const postsArray = Array.isArray(posts) ? posts : [];
  const headingFont = design?.typography?.headingFont || "'Sora', sans-serif";
  const bodyFont = design?.typography?.bodyFont || "'Plus Jakarta Sans', sans-serif";
  const primaryColor = design?.colors?.primary || '#E07A5F';
  const textColor = design?.colors?.text || '#1E1E1E';
  const borderRadius = design?.effects?.borderRadius || '8px';
  const boxShadow = design?.effects?.boxShadow || '0 5px 20px rgba(0,0,0,0.05)';
  const containerMaxWidth = design?.spacing?.containerMaxWidth || '1200px';

  const sectionStyle: React.CSSProperties = { padding: '60px 0' };
  const subtitleStyle: React.CSSProperties = { fontFamily: headingFont, color: primaryColor, textAlign: 'center' };
  const titleStyle: React.CSSProperties = { fontFamily: headingFont, fontSize: '2rem', color: textColor, textAlign: 'center', marginBottom: '1rem' };
  const descStyle: React.CSSProperties = { fontFamily: bodyFont, color: design?.colors?.textLight || '#6C757D', textAlign: 'center', maxWidth: '700px', margin: '0 auto 2rem' };
  const cardStyle: React.CSSProperties = { borderRadius, boxShadow, overflow: 'hidden', backgroundColor: '#fff', height: '100%' };
  const dateBadgeStyle: React.CSSProperties = { backgroundColor: primaryColor, color: '#fff', padding: '0.5rem', textAlign: 'center', position: 'absolute', top: '10px', left: '10px', borderRadius: '4px' };
  const readMoreStyle: React.CSSProperties = { color: primaryColor, textDecoration: 'none', fontWeight: 'bold' };

  if (postsArray.length === 0) {
    return <div style={sectionStyle}>Aucun article de blog.</div>;
  }

  return (
    <section className="s-blog" style={sectionStyle}>
      <div className="container" style={{ maxWidth: containerMaxWidth, margin: '0 auto' }}>
        <div className="row">
          <div className="col-12">
            <div className="block-text center">
              <p className="subtitle" style={subtitleStyle}>{subtitle}</p>
              <h3 className="title" style={titleStyle}>{title}</h3>
              <p className="text" style={descStyle}>{description}</p>
            </div>
            <Swiper modules={[Autoplay]} autoplay={{ delay: 5000 }} loop spaceBetween={30} slidesPerView={3} breakpoints={{ 640: { slidesPerView: 1 }, 768: { slidesPerView: 2 }, 1024: { slidesPerView: 3 } }}>
              {postsArray.map((post, idx) => (
                <SwiperSlide key={idx}>
                  <div className="blog-box" style={cardStyle}>
                    <div className="top-content" style={{ position: 'relative' }}>
                      <div className="image">
                        <Image src={post.image} alt="" width={400} height={300} style={{ width: '100%', height: 'auto' }} />
                      </div>
                      <div className="meta" style={dateBadgeStyle}>
                        <h4 style={{ margin: 0 }}>{post.day}</h4>
                        <p style={{ margin: 0 }}>{post.month}</p>
                      </div>
                      <Link href={post.link} className="h5 title" style={{ fontFamily: headingFont, color: textColor, display: 'block', padding: '0.5rem 1rem' }}>{post.title}</Link>
                    </div>
                    <div className="bottom-content" style={{ padding: '1rem' }}>
                      <ul style={{ display: 'flex', gap: '1rem', listStyle: 'none', padding: 0, marginBottom: '0.5rem' }}>
                        <li className="author"><a href="#" style={{ color: design?.colors?.textLight }}>by {post.author}</a></li>
                        <li className="category"><a href="#" style={{ color: design?.colors?.textLight }}>{post.category}</a></li>
                      </ul>
                      <div className="line" style={{ borderTop: `1px solid ${design?.colors?.border || '#ddd'}`, margin: '0.5rem 0' }}></div>
                      <p className="text" style={{ fontFamily: bodyFont, color: design?.colors?.textLight }}>{post.excerpt}</p>
                      <Link href={post.link} className="btn-read" style={readMoreStyle}>read more <i className="fa fa-arrow-right"></i></Link>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      </div>
    </section>
  );
}